import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { orderContractModel } from '../pageModels/OrderContractModel';
import { CachedImage } from '../../components/CachedImage';
import Loading from '../../components/Loading/Loading';
import BuyerLevelTag from '../../components/BuyerLevelTag';
import { DateFormat } from '../../utils/DateUtil';
import { AliOssUtil } from '../../utils/AliOssUtil';
import { OssScene } from '../../global/OssConfig';
import { globalStore } from '../../GlobalStore';
import { 
  ArrowLeftOutlined,
  FileTextOutlined,
  UserOutlined,
  CrownOutlined,
  ShoppingCartOutlined,
  FilePdfOutlined,
  CloseOutlined,
  CopyOutlined,
  PlusOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import SellerLevelTag from '../../components/SellerLevelTag';
import CreditShow from '../../components/CreditShow';
import { OrderContractStyle } from './OrderContractStyle';
import {
  ContractListStatus,
  getContractStatusColor,
  getContractStatusLabel,
  getPartySignStatusColor,
  getPartySignStatusLabel,
  getPdfContractListStatus,
  getPdfPartyDisplayName,
  getQuickContractListStatus,
  getQuickPartyDisplayName,
  isPdfPosterSigned,
  isPdfSignerSigned,
  isQuickPosterSigned,
  isQuickSignerSigned,
} from '../../utils/ContractUtil';
import { PdfContract, PdfSignMode, QuickContract } from '../../models/OrderContract';
import QuickContractAppModal from './QuickContractAppModal';
import {
  fetchPdfObjectUrl,
  revokePdfObjectUrl,
} from '../../utils/PdfPreviewUtil';

const OrderContractDetail: React.FC = observer(() => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const initialized = globalStore.initialized;
  const isLoggedIn = globalStore.isLoggedIn;
  const [pdfPreviewSrc, setPdfPreviewSrc] = useState('');
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [contractName, setContractName] = useState('');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pdfSignAppTipVisible, setPdfSignAppTipVisible] = useState(false);
  const [pdfSignMode, setPdfSignMode] = useState<PdfSignMode>(PdfSignMode.UNSIGNED);

  useEffect(() => {
    if (!initialized) return;
    if (!isLoggedIn) {
      globalStore.loginModalVisible = true;
      navigate('/', { replace: true });
    }
  }, [initialized, isLoggedIn, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    globalStore.selectedMenuKey = 'order-contract';
    globalStore.forceWhiteHeader = true;
    return () => {
      orderContractModel.reset();
      globalStore.forceWhiteHeader = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (!initialized || !isLoggedIn || !orderId) return;
    orderContractModel.fetchOrderDetail(orderId);
  }, [initialized, isLoggedIn, orderId]);

  const handleBack = () => {
    navigate('/order-contract');
  };

  const handlePdfClick = (contract: PdfContract) => {
    orderContractModel.showPdfPreview(contract);
  };

  useEffect(() => {
    if (!orderContractModel.pdfPreviewVisible) {
      setPdfSignAppTipVisible(false);
    }
  }, [orderContractModel.pdfPreviewVisible]);

  useEffect(() => {
    const contract = orderContractModel.selectedPdfContract;
    if (!orderContractModel.pdfPreviewVisible || !contract?.pdfUrl) {
      setPdfPreviewSrc((prev) => {
        revokePdfObjectUrl(prev);
        return '';
      });
      setPdfPreviewLoading(false);
      setPdfPreviewError('');
      return;
    }

    let cancelled = false;
    let objectUrl = '';
    setPdfPreviewLoading(true);
    setPdfPreviewError('');
    setPdfPreviewSrc('');

    fetchPdfObjectUrl(contract.pdfUrl)
      .then((url) => {
        if (cancelled) {
          revokePdfObjectUrl(url);
          return;
        }
        objectUrl = url;
        setPdfPreviewSrc(url);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : 'PDF 加载失败';
        setPdfPreviewError(message);
      })
      .finally(() => {
        if (!cancelled) setPdfPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) revokePdfObjectUrl(objectUrl);
    };
  }, [
    orderContractModel.pdfPreviewVisible,
    orderContractModel.selectedPdfContract?.contractId,
    orderContractModel.selectedPdfContract?.pdfUrl,
  ]);

  const getSpecificationText = (type: number) => {
    switch (type) {
      case 0: return '试用版';
      case 1: return '标准版';
      default: return '未知';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return '待支付';
      case 1: return '待开启服务';
      case 2: return '服务进行中';
      case 3: return '待验收';
      case 4: return '已完成';
      case 5: return '已取消';
      default: return '未知';
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return '#faad14';
      case 1: return '#1890ff';
      case 2: return '#52c41a';
      case 3: return '#722ed1';
      case 4: return '#52c41a';
      case 5: return '#ff4d4f';
      default: return '#8c8c8c';
    }
  };

  if (!initialized || orderContractModel.detailLoading) {
    return <Loading />;
  }

  if (!orderContractModel.selectedOrder) {
    return (
      <div style={{ padding: '120px 24px 24px', minHeight: '100vh', background: '#f5f5f5' }}>
        <div style={{
          maxWidth: '480px',
          margin: '0 auto',
          background: '#fff',
          padding: '32px',
          textAlign: 'center',
          borderRadius: OrderContractStyle.cardBorderRadius,
          border: '1px solid #e8e8e8',
        }}>
          <div style={{ fontSize: '16px', color: '#5b6472', marginBottom: '20px' }}>
            {orderContractModel.detailError || '订单不存在或无权访问'}
          </div>
          <button
            onClick={handleBack}
            style={{
              padding: '8px 20px',
              border: '1px solid #d9d9d9',
              borderRadius: OrderContractStyle.buttonBorderRadius,
              background: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#5b6472',
            }}
          >
            返回订单列表
          </button>
        </div>
      </div>
    );
  }

  const order = orderContractModel.selectedOrder;
  const currentUserId = globalStore.currentBuyer?.buyerId;
  const pdfCount = order.pdfContractList?.length || 0;
  const quickCount = order.quickContractList?.length || 0;

  const renderStatusBadge = (status: ContractListStatus) => (
    <span style={{
      fontSize: '11px',
      fontWeight: '500',
      color: getContractStatusColor(status),
      padding: '2px 6px',
      borderRadius: OrderContractStyle.tagBorderRadius,
      background: `${getContractStatusColor(status)}15`,
    }}>
      {getContractStatusLabel(status)}
    </span>
  );

  const renderPartyRow = (
    label: string,
    name: string,
    signed: boolean,
  ) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '13px',
        padding: '6px 0',
      }}
    >
      <span style={{ color: '#666' }}>
        {label}：<span style={{ color: '#333' }}>{name}</span>
      </span>
      <span style={{ color: getPartySignStatusColor(signed), fontWeight: '500' }}>
        {getPartySignStatusLabel(signed)}
      </span>
    </div>
  );

  const renderPdfContractCard = (contract: PdfContract) => {
    const status = getPdfContractListStatus(contract, currentUserId);
    const posterSigned = isPdfPosterSigned(contract);
    const signerSigned = isPdfSignerSigned(contract);
    return (
      <div
        key={contract.contractId}
        onClick={() => handlePdfClick(contract)}
        style={{
          border: '1px solid #e8e8e8',
          padding: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          background: '#fff',
          position: 'relative',
          borderRadius: OrderContractStyle.contractCardBorderRadius,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#1890ff';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(24,144,255,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e8e8e8';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
        }}>
          {renderStatusBadge(status)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', paddingRight: '72px' }}>
          <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
          <div style={{ flex: '1', minWidth: '0' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#0b1220',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {contract.contractName}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              合同号: {contract.contractNo}
            </div>
          </div>
        </div>
        {renderPartyRow(
          '发起人',
          getPdfPartyDisplayName(contract, 'poster', currentUserId),
          posterSigned,
        )}
        {renderPartyRow(
          '签署人',
          getPdfPartyDisplayName(contract, 'signer', currentUserId),
          signerSigned,
        )}
      </div>
    );
  };

  const renderQuickContractCard = (contract: QuickContract) => {
    const status = getQuickContractListStatus(contract, currentUserId);
    const posterSigned = isQuickPosterSigned(contract);
    const signerSigned = isQuickSignerSigned(contract);
    return (
      <div
        key={contract.contractId}
        onClick={() => orderContractModel.showQuickAppGuide(contract)}
        style={{
          border: '1px solid #ffd591',
          padding: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          background: '#fffbf0',
          position: 'relative',
          borderRadius: OrderContractStyle.contractCardBorderRadius,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#fa8c16';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(250,140,22,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#ffd591';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
        }}>
          {renderStatusBadge(status)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', paddingRight: '72px' }}>
          <ThunderboltOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
          <div style={{ flex: '1', minWidth: '0' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#0b1220',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {contract.contractName}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              合同号: {contract.contractNo}
            </div>
          </div>
        </div>
        {renderPartyRow(
          '发起人',
          getQuickPartyDisplayName(contract, 'poster', currentUserId),
          posterSigned,
        )}
        {renderPartyRow(
          '签署人',
          getQuickPartyDisplayName(contract, 'signer', currentUserId),
          signerSigned,
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      padding: '80px 24px 24px', 
      minHeight: '100vh',
      background: '#f5f5f5'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* 返回按钮 */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#5b6472',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#5b6472';
            }}
          >
            <ArrowLeftOutlined />
            返回订单列表
          </button>
        </div>

        {/* 订单基本信息卡片 */}
        <div style={{ 
          background: '#fff',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #e8e8e8',
          borderRadius: OrderContractStyle.cardBorderRadius,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          {/* 订单ID、买家信息和状态 - 放在最上面 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px'
              }}>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: '500'
                }}>
                  订单ID: {order.orderId}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(order.orderId);
                    alert('订单ID已复制到剪贴板');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#999',
                    fontSize: '12px'
                  }}
                  title="复制订单ID"
                >
                  <CopyOutlined />
                </button>
              </div>
              
              {/* 买家信息 */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  overflow: 'hidden',
                  border: '1px solid #f0f0f0',
                  borderRadius: '50%'
                }}>
                  {order.sellerInfo?.avatarUrl ? (
                    <CachedImage
                      url={order.sellerInfo.avatarUrl}
                      enablePreview={false}
                      style={{
                        display: 'block',
                        width: '24px',
                        height: '24px',
                        objectFit: 'cover'
                      }}
                      alt="卖家头像"
                    />
                  ) : (
                    <UserOutlined style={{ 
                      fontSize: '12px', 
                      color: '#bfbfbf',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      background: '#f5f5f5'
                    }} />
                  )}
                </div>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#666'
                }}>
                  {order.sellerInfo?.nickname || '未知用户'}
                </span>
                {order.sellerInfo?.sellerLevel && order.sellerInfo?.sellerCredit && (
                    <>  
                    <SellerLevelTag 
                      level={order.sellerInfo.sellerLevel} 
                      creditLevel={order.sellerInfo.sellerCredit}
                      size="small"
                    /> 
                    <CreditShow credit={order.sellerInfo.sellerCredit} fontSize={12} />
                  </> 
                )}
              </div>
            </div>
            
            {/* 订单状态 */}
            <div style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: getStatusColor(order.status),
              flexShrink: '0'
            }}>
              {getStatusText(order.status)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            {/* 商品图片 */}
            <div style={{ flexShrink: '0' }}>
              <CachedImage
                url={order.productSnapshot.headImageUrl}
                style={{
                  width: '120px',
                  height: '120px',
                  objectFit: 'cover',
                  borderRadius: OrderContractStyle.imageBorderRadius,
                  border: '1px solid #f0f0f0'
                }}
                alt={order.productSnapshot.name}
              />
            </div>

            {/* 订单信息 */}
            <div style={{ flex: '1', minWidth: '0' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ flex: '1', minWidth: '0' }}>
                  <h1 style={{ 
                    fontSize: '24px', 
                    fontWeight: '700', 
                    color: '#0b1220',
                    margin: '0 0 12px 0',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {order.productSnapshot.name}
                  </h1>
                  <p style={{ 
                    fontSize: '16px', 
                    color: '#5b6472',
                    margin: '0 0 16px 0',
                    lineHeight: '1.6'
                  }}>
                    {order.productSnapshot.description}
                  </p>
                </div>
              </div>

              {/* 订单详情 */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileTextOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontSize: '14px', color: '#5b6472' }}>
                    规格: {getSpecificationText(order.specificationType)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShoppingCartOutlined style={{ color: '#52c41a' }} />
                  <span style={{ fontSize: '14px', color: '#5b6472' }}>
                    数量: {order.quantity}
                  </span>
                </div> 
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#0b1220' }}>
                    ¥{order.price.toFixed(2)}
                  </span>
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* PDF合同列表 */}
        <div style={{ 
          background: '#fff',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #e8e8e8',
          borderRadius: OrderContractStyle.cardBorderRadius,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#0b1220',
              margin: '0'
            }}>
              PDF合同 ({pdfCount})
            </h2>
            
            <button
              onClick={() => setUploadModalVisible(true)}
              style={{
                padding: '8px 16px',
                border: '1px solid #1890ff',
                borderRadius: OrderContractStyle.buttonBorderRadius,
                background: '#fff',
                color: '#1890ff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1890ff';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#1890ff';
              }}
            >
              上传合同
            </button>
          </div>
          
          {orderContractModel.pdfContracts.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#8c8c8c'
            }}>
              暂无PDF合同
            </div>
          ) : (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px'
            }}>
              {orderContractModel.pdfContracts.map(renderPdfContractCard)}
            </div>
          )}
        </div>

        {/* 闪电合约列表 */}
        <div style={{ 
          background: '#fff',
          padding: '24px',
          border: '1px solid #e8e8e8',
          borderRadius: OrderContractStyle.cardBorderRadius,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#0b1220',
            margin: '0 0 20px 0'
          }}>
            闪电合约 ({quickCount})
          </h2>
          {orderContractModel.quickContracts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
              {quickCount > 0 ? '闪电合约加载失败，请刷新重试' : '暂无闪电合约'}
            </div>
          ) : (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px'
            }}>
              {orderContractModel.quickContracts.map(renderQuickContractCard)}
            </div>
          )}
        </div>
      </div>

      {/* PDF预览弹框 */}
      {orderContractModel.pdfPreviewVisible && orderContractModel.selectedPdfContract && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            width: '90%',
            maxWidth: '1000px',
            height: '90%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: OrderContractStyle.pdfPreviewModalBorderRadius,
          }}>
            {/* 弹框头部 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: '1px solid #e8e8e8',
              background: '#fafafa',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <FilePdfOutlined style={{ fontSize: '20px', color: '#ff4d4f', flexShrink: 0 }} />
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {orderContractModel.selectedPdfContract.contractName}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setPdfSignAppTipVisible(true)}
                  style={{
                    padding: '6px 16px',
                    border: 'none',
                    background: '#1890ff',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    borderRadius: OrderContractStyle.buttonBorderRadius,
                  }}
                >
                  签署
                </button>
                <button
                  type="button"
                  onClick={() => orderContractModel.hidePdfPreview()}
                  aria-label="关闭"
                  style={{
                    padding: '4px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#8c8c8c',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>

            {/* PDF内容区域 */}
            <div style={{ flex: '1', padding: '0', overflow: 'hidden', position: 'relative' }}>
              {pdfPreviewLoading && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8c8c8c',
                  fontSize: '14px',
                }}>
                  正在加载 PDF…
                </div>
              )}
              {!pdfPreviewLoading && pdfPreviewError && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  color: '#ff4d4f',
                  fontSize: '14px',
                  textAlign: 'center',
                }}>
                  {pdfPreviewError}
                </div>
              )}
              {!pdfPreviewLoading && pdfPreviewSrc && (
                <iframe
                  src={pdfPreviewSrc}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="PDF预览"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {pdfSignAppTipVisible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setPdfSignAppTipVisible(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              padding: '28px 32px',
              maxWidth: '400px',
              width: '100%',
              borderRadius: OrderContractStyle.pdfPreviewModalBorderRadius,
              textAlign: 'center',
            }}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#0b1220',
              margin: '0 0 12px 0',
            }}>
              请前往 APP 签署
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#ff4d4f',
              lineHeight: '1.6',
              margin: '0 0 24px 0',
            }}>
              WEB 端仅支持上传 和 浏览 PDF 合同，签署请在买家 APP 中完成。
            </p>
            <button
              type="button"
              onClick={() => setPdfSignAppTipVisible(false)}
              style={{
                padding: '8px 28px',
                border: '1px solid #d9d9d9',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                borderRadius: OrderContractStyle.buttonBorderRadius,
              }}
            >
              知道了
            </button>
          </div>
        </div>
      )}

      {orderContractModel.quickAppGuideVisible && orderContractModel.selectedQuickContract && (
        <QuickContractAppModal
          contract={orderContractModel.selectedQuickContract}
          currentUserId={currentUserId}
          onClose={() => orderContractModel.hideQuickAppGuide()}
        />
      )}

      {/* 上传合同弹框 */}
      {uploadModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            background: '#fff',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            borderRadius: OrderContractStyle.modalBorderRadius,
            overflow: 'hidden',
          }}>
            <button
              type="button"
              aria-label="关闭"
              onClick={() => {
                setUploadModalVisible(false);
                setContractName('');
                setSelectedFile(null);
                setPdfSignMode(PdfSignMode.UNSIGNED);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                padding: '4px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#8c8c8c',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseOutlined />
            </button>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#0b1220',
              margin: '0 0 16px 0'
            }}>
              上传合同
            </h3>

            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '16px',
              padding: '4px',
              background: '#f5f5f5',
              borderRadius: OrderContractStyle.cardBorderRadius,
            }}>
              {([
                { mode: PdfSignMode.UNSIGNED, label: '未签字PDF' },
                { mode: PdfSignMode.PRE_SIGNED, label: '已签字PDF' },
              ] as const).map(({ mode, label }) => {
                const active = pdfSignMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setPdfSignMode(mode)}
                    style={{
                      flex: 1,
                      padding: '10px 8px',
                      border: 'none',
                      borderRadius: OrderContractStyle.buttonBorderRadius,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: active ? '600' : '500',
                      color: active ? '#1890ff' : '#5b6472',
                      background: active ? '#fff' : 'transparent',
                      boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <p style={{
              fontSize: '13px',
              color: pdfSignMode === PdfSignMode.UNSIGNED ? '#1890ff' : '#ff4d4f',
              fontWeight: 500,
              lineHeight: '1.6',
              textAlign: 'left',
              margin: '0 0 20px 0',
            }}>
              {pdfSignMode === PdfSignMode.UNSIGNED
                ? '上传后需买卖双方在 APP 内完成签署。'
                : '适用于双方已线下签订的协议，上传作为凭证，无需在 APP 内再签。若为图片，请先使用转 PDF 工具转换后再上传。'}
            </p>
            
            <div style={{ textAlign: 'left', marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#333',
                marginBottom: '8px'
              }}>
                合同名称
              </label>
              <input
                type="text"
                value={contractName}
                onChange={(e) => setContractName(e.target.value)}
                maxLength={15}
                placeholder="请输入合同名称（最多15字）"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d9d9d9',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  borderRadius: OrderContractStyle.buttonBorderRadius,
                }}
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '4px',
                textAlign: 'right'
              }}>
                {contractName.length}/15
              </div>
            </div>
            
            <div style={{ 
              border: '2px dashed #d9d9d9',
              padding: '40px 20px',
              marginBottom: '24px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: selectedFile ? '#f6ffed' : 'transparent',
              borderColor: selectedFile ? '#52c41a' : '#d9d9d9',
              borderRadius: OrderContractStyle.cardBorderRadius,
            }}
            onMouseEnter={(e) => {
              if (!selectedFile) {
                e.currentTarget.style.borderColor = '#1890ff';
                e.currentTarget.style.background = '#f0f8ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!selectedFile) {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.background = 'transparent';
              }
            }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf';
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                if (target.files && target.files[0]) {
                  setSelectedFile(target.files[0]);
                }
              };
              input.click();
            }}
            >
              {selectedFile ? (
                <>
                  <FilePdfOutlined style={{ 
                    fontSize: '32px', 
                    color: '#52c41a',
                    marginBottom: '8px'
                  }} />
                  <div style={{ fontSize: '14px', color: '#52c41a', fontWeight: '500' }}>
                    {selectedFile.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>
                    点击重新选择
                  </div>
                </>
              ) : (
                <>
                  <PlusOutlined style={{ 
                    fontSize: '32px', 
                    color: '#d9d9d9',
                    marginBottom: '8px'
                  }} />
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    点击添加PDF
                  </div>
                </>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button
                onClick={() => {
                  setUploadModalVisible(false);
                  setContractName('');
                  setSelectedFile(null);
                  setPdfSignMode(PdfSignMode.UNSIGNED);
                }}
                style={{
                  flex: 1,
                  padding: '10px 24px',
                  border: '1px solid #d9d9d9',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#5b6472',
                  borderRadius: OrderContractStyle.buttonBorderRadius,
                }}
              >
                取消
              </button>
              <button
                onClick={() => {
                  if (!contractName.trim()) {
                    alert('请输入合同名称');
                    return;
                  }
                  if (!selectedFile) {
                    alert('请选择PDF文件');
                    return;
                  }
                  setUploadModalVisible(false);
                  setConfirmModalVisible(true);
                }}
                style={{
                  flex: 1,
                  padding: '10px 24px',
                  border: '1px solid #1890ff',
                  background: '#1890ff',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: OrderContractStyle.buttonBorderRadius,
                }}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 上传中遮罩层 */}
      {uploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 1003,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #1890ff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            color: '#fff',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            正在上传中...
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* 确认提交弹框 */}
      {confirmModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            borderRadius: OrderContractStyle.modalBorderRadius,
            overflow: 'hidden',
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#0b1220',
              margin: '0 0 24px 0'
            }}>
              确认提交
            </h3>
            
            <p style={{ 
              fontSize: '14px', 
              color: '#5b6472',
              lineHeight: '1.6',
              margin: '0 0 24px 0'
            }}>
              {pdfSignMode === PdfSignMode.PRE_SIGNED
                ? '确定上传该已签字 PDF 作为凭证吗？上传后将无法删除，请确认文件准确无误。'
                : '确定提交本合同到此订单吗？合同上传后将无法删除，请务必确认内容准确后再上传！上传后需双方在 APP 内签署。'}
            </p>
            
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button
                onClick={() => {
                  setConfirmModalVisible(false);
                  setContractName('');
                  setSelectedFile(null);
                  setPdfSignMode(PdfSignMode.UNSIGNED);
                }}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '10px 24px',
                  border: '1px solid #d9d9d9',
                  background: '#fff',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: uploading ? '#bfbfbf' : '#5b6472',
                  opacity: uploading ? 0.6 : 1,
                  borderRadius: OrderContractStyle.buttonBorderRadius,
                }}
              >
                取消
              </button>
              <button
                onClick={async () => {
                  if (!selectedFile) {
                    alert('请选择PDF文件');
                    return;
                  }
                  
                  setUploading(true);
                  try {
                    // 上传文件到OSS
                    const pdfUrl = await AliOssUtil.uploadFile(
                      selectedFile,
                      OssScene.pdfContract,
                    );
                    
                    //这里调用addPdfContract方法，传入PDF URL
                    await orderContractModel.addPdfContract(
                      order.orderId,
                      contractName,
                      pdfUrl,
                      pdfSignMode,
                    );
                    
                    setConfirmModalVisible(false);
                    setContractName('');
                    setSelectedFile(null);
                    setPdfSignMode(PdfSignMode.UNSIGNED);
                    setUploading(false);
                     
                  } catch (error: any) {
                    setUploading(false);
                    alert('上传失败：' + (error.message || '未知错误'));
                  }
                }}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '10px 24px',
                  border: '1px solid #1890ff',
                  background: uploading ? '#bfbfbf' : '#1890ff',
                  color: '#fff',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: uploading ? 0.6 : 1,
                  borderRadius: OrderContractStyle.buttonBorderRadius,
                }}
              >
                {uploading ? '上传中...' : '确认提交'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default OrderContractDetail; 